import React, { useEffect, useRef, useState } from "react";
import { useCombinedStore } from "../store";
import Modal from "react-modal";
import MessageBox from "./MessageBox";
import * as Styles from "../styles/PeriodOrders.Modal.styles";
import alfredoIcon from "../assets/alfredo-icon.svg";
import api from "../services/api.service";
import { useParams } from "react-router-dom";
import { infosDoTipo } from "../dominio/conta.tipos";
import {
  descontoDaConta,
  descontoDosProdutos,
  taxaPorTipo,
  taxaPorTipoNaMesa,
  taxasDaConta,
  totalDeTaxas,
  totalDosProdutosBruto,
} from "../dominio/totaisDaConta";

interface PeriodOrdersModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  saleType: number;
}

const PeriodOrdersModal: React.FC<PeriodOrdersModalProps> = ({
  isOpen,
  onRequestClose,
  saleType,
}) => {
  const { saleOption } = useParams();

  const {
    subsidiaryDataZustand,
  } = useCombinedStore((state) => ({
    subsidiaryDataZustand: state.subsidiaryDataZustand,
  }));

  const modalWrapperRef = useRef<HTMLDivElement>(null);

  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const selectedSaleOption =
    subsidiaryDataZustand.selectedPdv.saleOptions.filter(
      (item) => item.itemInfoUuid === saleOption
    )[0];
  const handlePrintOrder = async (orderUuid: string) => {
    if (orderUuid === "") {
      return;
    }
    api.post(`printOrder/${orderUuid}/${selectedSaleOption.itemInfoValue.saleOptionPrinterAddress}`);
  };

  const handleCheckboxChange = (orderUuid: string) => {

    const periodOrders = subsidiaryDataZustand.selectedPdv.selectedPeriod?.periodInfo?.periodOrders;
    const selectedOrder = periodOrders?.find((order: any) => order.orderUuid === orderUuid);
    const products = infosDoTipo(selectedOrder.orderInfo, 3);

    // Base BRUTA (`productPrice` + `productDiscount`) — é a que este modal
    // sempre usou, a mesma do cupom impresso e, desde a decisão do card 25,
    // a base ÚNICA do desconto no sistema inteiro (`baseDoDesconto`).
    // Aqui ela é também a base das taxas deste modal, que já era assim.
    const productTotals = totalDosProdutosBruto(products);

    // `taxasDaConta` nunca devolve undefined: conta sem linha tipo 7 vira [].
    // Antes daqui saía `let orderTaxs: any;` sem valor e o `.reduce` logo
    // abaixo derrubava o modal (card 25, bug (c)).
    const orderTaxs = taxasDaConta(selectedOrder.orderInfo);
    const calculateTaxValue = (taxType: string) =>
      taxaPorTipo(orderTaxs, productTotals, taxType);

    // Semântica PRÓPRIA deste modal, preservada: `totalDiscount` aqui soma o
    // desconto de PRODUTO com o desconto da CONTA. No PDV é só o da conta.
    const orderDiscount =
      descontoDosProdutos(products) +
      descontoDaConta(selectedOrder.orderInfo, productTotals);

    const orderPayments = selectedOrder.orderInfo.filter(
      (info: any) => info.orderInfoType === 10
    );

    const totalCashPayments = orderPayments.reduce((acc: any, payment: any) => {
      const paymentData = JSON.parse(payment.orderInfoValue);
      const paymentValue = parseFloat(paymentData.value.replace(",", "."));
      return isNaN(paymentValue) ? acc : acc + paymentValue;
    }, 0);

    const totals = {
      totalProductsPrice: Number(productTotals)
        .toFixed(2)
        .replace(".", ","),
      totalDiscount: Number(orderDiscount)
        .toFixed(2)
        .replace(".", ","),
      totalProductsPriceWithDiscount: Number(
        productTotals - orderDiscount
      )
        .toFixed(2)
        .replace(".", ","),
      serviceTax: calculateTaxValue("1").toFixed(2).replace(".", ","),
      entranceTax: calculateTaxValue("2")
        .toFixed(2)
        .replace(".", ","),
      minimumConsumption: calculateTaxValue("3")
        .toFixed(2)
        .replace(".", ","),
      parkTax: calculateTaxValue("4").toFixed(2).replace(".", ","),
      deliveryTax: calculateTaxValue("5")
        .toFixed(2)
        .replace(".", ","),
      customerBalance: "0",
      total: String(
        (
          productTotals -
          orderDiscount +
          totalDeTaxas(orderTaxs, productTotals)
        ).toFixed(2)
      ).replace(".", ","),
      totalCashPayments: totalCashPayments.toFixed(2).replace(".", ","),
    };
    setSelectedOrder({
      orderUuid: selectedOrder.orderUuid,
      orderCode: selectedOrder.orderCode,
      orderOwnerUuid:
        selectedOrder.updatedBy,
      orderOwnerName:
        selectedOrder.updatedByUuid
          .userSocialName,
      orderStatus:
        selectedOrder.orderStatus,
      orderPrinted: false,
      orderType: selectedOrder.orderType,
      createdAt: selectedOrder.createdAt,
      orderInfo: {
        orderTaxs: [...orderTaxs],
      },
      products:
        products.map((product: any) => {
          const productInfo = JSON.parse(product.orderInfoValue);
          const productObservations =
            productInfo.productObservations?.map(
              (observation: any) => {
                return {
                  observationUuid: observation.observationUuid,
                  observationName: observation.observationName,
                  observationPrice: observation.observationPrice,
                };
              }
            );
          return {
            productOrderUuid: product.orderInfoUuid,
            productSelected: false,
            productSemiSelected: false,
            productUuid: productInfo.productUuid,
            productCode: productInfo.productCode,
            productPrinted: productInfo.productPrinted,
            productName: productInfo.productName,
            productPrice: Number(productInfo.productPrice)
              .toFixed(2)
              .replace(".", ","),
            productDiscount:
              Number(productInfo.productDiscount || 0)
                .toFixed(2)
                .replace(".", ",") ?? "0,00",
            productTotalPrice: (
              Number(productInfo.productPrice) +
              Number(productInfo.productDiscount || 0)
            )
              .toFixed(2)
              .replace(".", ","),
            productOrderedAt: formatTime(String(product.createdAt)),
            productObservations: productObservations,
          };
        }) ?? [],
      totals: totals,
    });
  };

  function formatTime(timeString: any) {
    const date = new Date(timeString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    return `${hours}:${minutes}:${seconds}`;
  }
  const [filteredOrders, setFilteredOrders] = useState<any[]>([]);
  const [totalBilling, setTotalBilling] = useState<number>(0);
  const [totalServiceFee, setTotalServiceFee] = useState<number>(0);
  const [repique, setRepique] = useState<number>(0);

  useEffect(() => {
    if (!isOpen) return;

    const filteredOrders = subsidiaryDataZustand.selectedPdv.selectedPeriod.periodInfo?.periodOrders?.filter(
      (order: any) => Number(order.orderType) === Number(saleType) && Number(order.orderStatus) === 6
    );
    setFilteredOrders(filteredOrders);

    const billingTotal = filteredOrders?.reduce((acc: any, order: any) => {
      // Calcula os totais dos produtos (base BRUTA, como este modal sempre fez)
      const products = infosDoTipo(order.orderInfo, 3);
      const productTotals = totalDosProdutosBruto(products);

      // Calcula os descontos individuais dos produtos
      const orderDiscounts = descontoDosProdutos(products);

      // Calcula os impostos da ordem (conta sem linha tipo 7 soma 0)
      const taxTotal = totalDeTaxas(taxasDaConta(order.orderInfo), productTotals);

      // Desconto da conta inteira (orderInfoType === 8), pela função única
      const accountDiscount = descontoDaConta(order.orderInfo, productTotals);

      // Calcula o total da ordem considerando todos os descontos e impostos
      return acc + (productTotals - orderDiscounts - accountDiscount + taxTotal);
    }, 0) || 0;

    setTotalBilling(billingTotal);


    const serviceFeeTotal = filteredOrders?.reduce((acc: any, order: any) => {
      // Antes daqui saía `(totalProductsValue * 10) / 100` — 10% CRAVADO no
      // código, ignorando o `taxValue` real da taxa de serviço do período.
      // Agora usa o valor cadastrado, lugar a lugar, exatamente como a tela
      // da mesa no PDV calcula (`taxPeople`). Card 25.
      const products = infosDoTipo(order.orderInfo, 3);
      const lugares = Number(
        JSON.parse(infosDoTipo(order.orderInfo, 2)[0]?.orderInfoValue || "1")
      ) || 1;
      const taxValue = taxaPorTipoNaMesa(
        taxasDaConta(order.orderInfo),
        products,
        lugares,
        "1"
      );

      return acc + (isNaN(taxValue) ? 0 : taxValue);
    }, 0) || 0;

    setTotalServiceFee(serviceFeeTotal);


    const paymentsTotal = filteredOrders?.reduce((acc: any, order: any) => {
      const payments = order.orderInfo?.filter((info: any) => info.orderInfoType === 10);

      const totalOrderPayments = payments.reduce((sum: number, payment: any) => {
        return sum + parseFloat(JSON.parse(payment.orderInfoValue).value);
      }, 0);

      return acc + totalOrderPayments;
    }, 0) || 0;



    const repiqueTotal = Number((paymentsTotal - billingTotal).toFixed(2));
    setRepique(repiqueTotal);
  }, [isOpen, saleType, subsidiaryDataZustand.selectedPdv.selectedPeriod.periodInfo?.periodOrders]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={Styles.customStyles}
      contentLabel="Main Modal"
      ariaHideApp={false}
    >
      <MessageBox />
      <Styles.ModalWrapper tabIndex={-1} ref={modalWrapperRef}>
        <Styles.CloseButton onClick={() => { setSelectedOrder(null); onRequestClose() }}>&times;</Styles.CloseButton>
        <Styles.ModalContent>
          <Styles.ModalHeader
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .buttonsColor
            }
          >
            <Styles.Logo src={alfredoIcon} alt="Alfredo Web OPS" />
            <Styles.ModalTitle>Contas do Período</Styles.ModalTitle>
            <Styles.ModalDescription>
              Consulte as informações das contas do período atual.
            </Styles.ModalDescription>
          </Styles.ModalHeader>
          <Styles.ModalBody
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .backgroundColor
            }
          >
            <Styles.RowContainer>
              <Styles.TableContainer>
                <Styles.Table>
                  <thead>
                    <tr>
                      <th style={{ width: "10%" }}>Selecione</th>
                      <th style={{ width: "20%" }}>Tipo de Venda</th>
                      <th style={{ width: "20%" }}>Vendedor</th>
                      <th style={{ width: "20%" }}>Código da Conta</th>
                      <th style={{ width: "10%" }}>Horário</th>
                      <th style={{ width: "20%" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders?.map(
                      (order: any, index: number) => {
                        const products = infosDoTipo(order.orderInfo, 3);

                        // Mesma conta do bloco de cima, pela mesma função.
                        // Antes era uma cópia literal, com `let orderTaxs: any;`
                        // sem valor e `.reduce` sem guarda logo abaixo.
                        const productTotals = totalDosProdutosBruto(products);
                        const orderDiscount =
                          descontoDosProdutos(products) +
                          descontoDaConta(order.orderInfo, productTotals);
                        const orderTaxs = taxasDaConta(order.orderInfo);
                        return (
                          <Styles.TableRow
                            key={order.orderUuid}
                            onClick={() => handleCheckboxChange(order.orderUuid)}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedOrder?.orderUuid === order.orderUuid}
                              />
                            </td>
                            <td>
                              {order.orderType === 1
                                ? "Atendimento Local"
                                : order.orderType === 2
                                  ? "Retirada"
                                  : order.orderType === 3
                                    ? "Entrega"
                                    : "Outros"}
                            </td>
                            <td>{order.updatedByUuid.userSocialName}</td>
                            <td>{order.orderCode}</td>
                            <td>{formatTime(order.createdAt)}</td>
                            <td>{String(
                              (
                                productTotals -
                                orderDiscount +
                                totalDeTaxas(orderTaxs, productTotals)
                              ).toFixed(2)
                            ).replace(".", ",")}</td>
                          </Styles.TableRow>
                        );
                      }
                    )}
                  </tbody>
                  <tfoot style={{ backgroundColor: subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue.backgroundColor }}>
                    <tr>
                      <td colSpan={3}>Total de Atendimentos: {filteredOrders?.length}</td>
                      <td colSpan={6}>Total de Faturamento: {totalBilling.toFixed(2).replace(".", ",")} </td>
                    </tr>
                    <tr>
                      {saleType === 1 && (
                        <td colSpan={3}>Total de Taxa de Serviço: {totalServiceFee.toFixed(2).replace(".", ",")}</td>
                      )}
                      <td colSpan={6}>Repique: {repique.toFixed(2).replace(".", ",")}</td>
                    </tr>
                  </tfoot>
                </Styles.Table>
              </Styles.TableContainer>
            </Styles.RowContainer>
            {selectedOrder && (
              <Styles.RowContainer>
                <Styles.TableWrapper>
                  <Styles.ProductTable>
                    <Styles.ProductTableHeader>
                      <Styles.ProductTableHeaderCell>
                        <Styles.ExpandIcon
                          onClick={() => {
                            const updatedProducts = selectedOrder.products?.map(
                              (currentProduct: any) => {
                                return {
                                  ...currentProduct,
                                  productExpanded: !currentProduct.productExpanded,
                                };
                              }
                            );
                            setSelectedOrder({
                              ...selectedOrder,
                              products: updatedProducts,
                            });
                          }}
                        >
                          {selectedOrder?.products?.every(
                            (product: any) => product.productExpanded
                          )
                            ? "expand_less"
                            : "expand_more"}
                        </Styles.ExpandIcon>
                      </Styles.ProductTableHeaderCell>
                      <Styles.ProductTableHeaderCell style={
                        {
                          width: "40%"
                        }
                      }>
                        Descrição
                      </Styles.ProductTableHeaderCell>
                      <Styles.ProductTableHeaderCell>
                        Hora
                      </Styles.ProductTableHeaderCell>
                      <Styles.ProductTableHeaderCell style={
                        {
                          width: "20%"
                        }
                      }>
                        Preço
                      </Styles.ProductTableHeaderCell>
                      <Styles.ProductTableHeaderCell>
                        Desconto
                      </Styles.ProductTableHeaderCell>
                      <Styles.ProductTableHeaderCell style={
                        {
                          width: "20%"
                        }
                      }>
                        Total
                      </Styles.ProductTableHeaderCell>
                    </Styles.ProductTableHeader>
                    <Styles.ProductTableBody>
                      {selectedOrder?.products?.map((product: any) => (
                        <React.Fragment key={product.productOrderUuid}>
                          <Styles.ProductTableRow>
                            <Styles.ProductTableCell>
                              <Styles.ExpandIcon
                                onClick={(event) => {
                                  event.stopPropagation();
                                  const updatedProducts = selectedOrder.products?.map(
                                    (currentProduct: any) => {
                                      if (
                                        currentProduct.productOrderUuid ===
                                        product.productOrderUuid
                                      ) {
                                        return {
                                          ...currentProduct,
                                          productExpanded: !currentProduct.productExpanded,
                                        };
                                      }
                                      return currentProduct;
                                    }
                                  );
                                  setSelectedOrder({
                                    ...selectedOrder,
                                    products: updatedProducts,
                                  });
                                }}
                              >
                                {product.productExpanded ? "expand_less" : "expand_more"}
                              </Styles.ExpandIcon>
                            </Styles.ProductTableCell>
                            <Styles.ProductTableCell>
                              {product.productName}
                            </Styles.ProductTableCell>
                            <Styles.ProductTableCell>
                              {product.productOrderedAt}
                            </Styles.ProductTableCell>
                            <Styles.ProductTableCell>
                              R$ {product.productPrice}
                            </Styles.ProductTableCell>
                            <Styles.ProductTableCell>
                              R$ {product.productDiscount}
                            </Styles.ProductTableCell>
                            <Styles.ProductTableCell>
                              R$ {product.productTotalPrice}
                            </Styles.ProductTableCell>
                          </Styles.ProductTableRow>
                          {product.productExpanded && (
                            <Styles.ProductTableRow>
                              <Styles.ProductTableCell colSpan={6}>
                                {product.productObservations?.length > 0 && (
                                  <>
                                    <Styles.ProductTableRowObservation>
                                      <Styles.ProductTableCellObservation>
                                        Observações:
                                      </Styles.ProductTableCellObservation>
                                    </Styles.ProductTableRowObservation>
                                    {product.productObservations.map(
                                      (observation: any) => (
                                        <Styles.ProductTableRowObservation key={observation.observationId}>
                                          <Styles.ProductTableCellObservation>
                                            {observation.observationName}
                                          </Styles.ProductTableCellObservation>
                                        </Styles.ProductTableRowObservation>
                                      )
                                    )}
                                  </>
                                )}
                              </Styles.ProductTableCell>
                            </Styles.ProductTableRow>
                          )}
                        </React.Fragment>
                      ))}

                    </Styles.ProductTableBody>

                  </Styles.ProductTable>
                </Styles.TableWrapper>
                <Styles.TotalTable>
                  <Styles.TotalTableHeader>
                    <Styles.TotalTableHeaderRow>
                      <Styles.TotalTableHeaderCell>
                        Totais
                      </Styles.TotalTableHeaderCell>
                    </Styles.TotalTableHeaderRow>
                  </Styles.TotalTableHeader>
                  <Styles.TotalTableBody>
                    <Styles.TotalTableRow>
                      <Styles.TotalTableCell>Total Produtos</Styles.TotalTableCell>
                      <Styles.TotalTableCell>
                        {selectedOrder.totals &&
                          selectedOrder.totals.hasOwnProperty("totalProductsPrice" as keyof typeof selectedOrder.totals)
                          ? `R$ ${selectedOrder.totals[
                          "totalProductsPrice" as keyof typeof selectedOrder.totals]}`
                          : "R$ 0,00"}
                      </Styles.TotalTableCell>
                    </Styles.TotalTableRow>
                    <Styles.TotalTableRow>
                      <Styles.TotalTableCell>Desconto</Styles.TotalTableCell>
                      <Styles.TotalTableCell>
                        {selectedOrder.totals &&
                          selectedOrder.totals.hasOwnProperty("totalDiscount" as keyof typeof selectedOrder.totals)
                          ? `R$ ${selectedOrder.totals[
                          "totalDiscount" as keyof typeof selectedOrder.totals]}`
                          : "R$ 0,00"}
                      </Styles.TotalTableCell>
                    </Styles.TotalTableRow>
                    <Styles.TotalTableRow>
                      <Styles.TotalTableCell>Taxa de Serviço</Styles.TotalTableCell>
                      <Styles.TotalTableCell>
                        {selectedOrder.totals &&
                          selectedOrder.totals.hasOwnProperty("serviceTax" as keyof typeof selectedOrder.totals)
                          ? `R$ ${selectedOrder.totals[
                          "serviceTax" as keyof typeof selectedOrder.totals]}`
                          : "R$ 0,00"}
                      </Styles.TotalTableCell>
                    </Styles.TotalTableRow>
                    <Styles.TotalTableRow>
                      <Styles.TotalTableCell>Taxa de Entrega</Styles.TotalTableCell>
                      <Styles.TotalTableCell>
                        {selectedOrder.totals &&
                          selectedOrder.totals.hasOwnProperty("deliveryTax" as keyof typeof selectedOrder.totals)
                          ? `R$ ${selectedOrder.totals[
                          "deliveryTax" as keyof typeof selectedOrder.totals]}`
                          : "R$ 0,00"}
                      </Styles.TotalTableCell>
                    </Styles.TotalTableRow>
                    <Styles.TotalTableRow>
                      <Styles.TotalTableCell>Taxa de Entrada</Styles.TotalTableCell>
                      <Styles.TotalTableCell>
                        {selectedOrder.totals &&
                          selectedOrder.totals.hasOwnProperty("entranceTax" as keyof typeof selectedOrder.totals)
                          ? `R$ ${selectedOrder.totals[
                          "entranceTax" as keyof typeof selectedOrder.totals]}`
                          : "R$ 0,00"}
                      </Styles.TotalTableCell>
                    </Styles.TotalTableRow>
                    <Styles.TotalTableRow>
                      <Styles.TotalTableCell>Total</Styles.TotalTableCell>
                      <Styles.TotalTableCell>
                        {selectedOrder.totals &&
                          selectedOrder.totals.hasOwnProperty("total" as keyof typeof selectedOrder.totals)
                          ? `R$ ${selectedOrder.totals[
                          "total" as keyof typeof selectedOrder.totals]}`
                          : "R$ 0,00"}
                      </Styles.TotalTableCell>
                    </Styles.TotalTableRow>
                    <Styles.TotalTableRow>
                      <Styles.TotalTableCell>Pagamentos</Styles.TotalTableCell>
                      <Styles.TotalTableCell>
                        {selectedOrder.totals &&
                          selectedOrder.totals.hasOwnProperty("totalCashPayments" as keyof typeof selectedOrder.totals)
                          ? `R$ ${selectedOrder.totals[
                          "totalCashPayments" as keyof typeof selectedOrder.totals]}`
                          : "R$ 0,00"}
                      </Styles.TotalTableCell>
                    </Styles.TotalTableRow>
                  </Styles.TotalTableBody>
                </Styles.TotalTable>
              </Styles.RowContainer>
            )}
          </Styles.ModalBody>
          <Styles.ModalFooter
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .buttonsColor
            }
          >
            <Styles.Button
              onClick={() => handlePrintOrder(selectedOrder?.orderUuid || "")}
              disabled={!selectedOrder}
            >
              Imprimir
            </Styles.Button>
            <Styles.Button onClick={() => { setSelectedOrder(null); onRequestClose(); }}>Fechar</Styles.Button>          </Styles.ModalFooter>
        </Styles.ModalContent>
      </Styles.ModalWrapper>
    </Modal >
  );
};

export default PeriodOrdersModal;
